import { useState, useEffect, useCallback } from "react"
import { Accordion, Badge, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { isSuperAdmin } from "../../common/helpers"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import SuperAdminBackLink from "../../components/platform/SuperAdminBackLink"
import moment from "moment"
import { DispatchTopProductsBySeller } from "../../types/Indicators.types"

interface Props {
  embedded?: boolean
}

interface OrgOption { id: string; name: string }

export default function DispatchTopProductsBySellerPage({ embedded = false }: Props) {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const canSelectOrg = isSuperAdmin(user)

  const [isLoading, setIsLoading] = useState(false)
  const [organizations, setOrganizations] = useState<OrgOption[]>([])
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)
  const [selectedOrgId, setSelectedOrgId] = useState(canSelectOrg ? "" : userOrgId)
  const [data, setData] = useState<DispatchTopProductsBySeller[]>([])
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"))
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))

  const effectiveOrgId = canSelectOrg ? selectedOrgId : userOrgId

  const loadOrgs = useCallback(async () => {
    if (!canSelectOrg) return
    try {
      setIsLoadingOrgs(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status === "ok") setOrganizations(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingOrgs(false) }
  }, [canSelectOrg])

  const bindData = useCallback(async (start: string, end: string) => {
    if (!effectiveOrgId) return
    try {
      setIsLoading(true)
      setStartDate(start)
      setEndDate(end)
      const params = `organizationId=${effectiveOrgId}&startDate=${start}&endDate=${end}`
      const res = await API.getAction("Indicators/DispatchTopProductsBySeller", params)
      if (res.data.status === "ok") setData(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoading(false) }
  }, [effectiveOrgId])

  useEffect(() => { loadOrgs() }, [loadOrgs])

  useEffect(() => {
    setData([])
    if (effectiveOrgId) bindData(startDate, endDate)
  }, [effectiveOrgId])

  return (
    <Container fluid className="px-4 py-3">
      {!embedded && <SuperAdminBackLink />}

      <h5 className="mb-3">
        <i className="fas fa-user-tie me-2 text-primary" />
        {t("Top Products by Seller")}
      </h5>

      {canSelectOrg && (
        <Row className="g-2 mb-3">
          <Col md={4} lg={3}>
            <Form.Select
              size="sm"
              value={selectedOrgId}
              onChange={e => setSelectedOrgId(e.target.value)}
              disabled={isLoadingOrgs}
            >
              {!selectedOrgId && <option value="">{t("Select Organization")}</option>}
              {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Form.Select>
          </Col>
        </Row>
      )}

      <DateRangeFilter onFilter={(s, e) => bindData(s, e)} loading={isLoading} />

      {canSelectOrg && !selectedOrgId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load dispatch indicators")}
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : data.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("No data available")}
          </Card.Body>
        </Card>
      ) : (
        <Accordion>
          {data.map((seller, i) => (
            <Accordion.Item key={seller.externalSellerId} eventKey={String(i)}>
              <Accordion.Header>
                <span className="fw-semibold me-3">{seller.sellerName}</span>
                <Badge bg="primary" className="me-2">
                  {seller.totalQuantity.toLocaleString()} {t("Total Qty")}
                </Badge>
                <Badge bg="secondary">
                  {seller.dispatchCount} {t("Dispatches")}
                </Badge>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                {seller.topProducts.length === 0 ? (
                  <div className="text-center text-muted py-3 small">
                    {t("No data available")}
                  </div>
                ) : (
                  <Table hover className="mb-0 small">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Code")}</th>
                        <th>{t("Product")}</th>
                        <th>{t("Brand")}</th>
                        <th className="text-end">{t("Quantity")}</th>
                        <th className="text-end pe-3">{t("Dispatches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seller.topProducts.map((p, pi) => (
                        <tr key={pi}>
                          <td className="ps-3 text-muted">{p.itemCode}</td>
                          <td>{p.description}</td>
                          <td>
                            {p.brand && (
                              <Badge bg="light" text="dark">{p.brand}</Badge>
                            )}
                          </td>
                          <td className="text-end">{p.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3">
                            <Badge bg="light" text="dark">{p.dispatchCount}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </Container>
  )
}
